import { useEffect, useState } from 'react'
import axios from 'axios'
import { useTheme } from '@table-library/react-table-library/theme'
import {
  DEFAULT_OPTIONS,
  getTheme,
} from '@table-library/react-table-library/material-ui'
import { usePagination } from '@table-library/react-table-library/pagination'
import { useSnackbar } from 'react-simple-snackbar'

import { HeaderLarge } from '../../../components/typography/Header'
import { TABLE_THEME, SERVER_URL, SNACKBAR_STYLE } from '../../../constants'
import Button from '../../../components/Button'
import ManageTable from './ManageTable'
import { FaPen, FaPlus } from 'react-icons/fa'
import Modal from '../../../components/Modal'

function ManageEmployees() {
  const [openSnackbar] = useSnackbar(SNACKBAR_STYLE)

  const [isLoading, setIsLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showUpdate, setShowUpdate] = useState(false)

  const [currId, setCurrId] = useState('')
  const [empName, setEmpName] = useState('')
  const [empEmail, setEmpEmail] = useState('')
  const [empPhone, setEmpPhone] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [salary, setSalary] = useState('')

  const [storedData, setStoredData] = useState({ nodes: [] })
  const [displayedData, setDisplayedData] = useState({ nodes: [] })

  useEffect(() => {
    renderTable()
  }, [])

  const fields = [
    {
      label: 'Name',
      type: 'input',
      value: empName,
      setValue: setEmpName,
    },
    {
      label: 'Email',
      type: 'input',
      value: empEmail,
      setValue: setEmpEmail,
    },
    {
      label: 'Phone Number',
      type: 'input',
      value: empPhone,
      setValue: setEmpPhone,
    },
    {
      label: 'Job Title',
      type: 'select',
      value: jobTitle,
      setValue: setJobTitle,
      options: ['Cashier', 'Janitor', 'Manager', 'Burrito Master'],
    },
    {
      label: 'Salary',
      type: 'input',
      value: salary,
      setValue: setSalary,
    },
  ]

  const addButtonProps = {
    isLoading: isLoading,
    handleClick: () => addEmployee(),
  }

  const updateButtonProps = {
    isLoading: isLoading,
    handleClick: () => updateEmployee(),
  }

  const columns = [
    { label: 'Name', renderCell: (item) => item.employee_name },
    { label: 'Email', renderCell: (item) => item.employee_email },
    { label: 'Phone Number', renderCell: (item) => item.employee_phone },
    {
      label: 'Job Title',
      renderCell: (item) =>
        item.job_title.charAt(0).toUpperCase() + item.job_title.slice(1),
    },
    { label: 'Salary', renderCell: (item) => item.hourly_salary },
    {
      label: (
        <Button
          size={'sm'}
          type={'icon'}
          onClick={() => {
            setEmpName('')
            setEmpEmail('')
            setEmpPhone('')
            setJobTitle('')
            setSalary('')
            setShowAdd(true)
          }}
        >
          <FaPlus size={15} />
        </Button>
      ),
      renderCell: (item) => {
        return (
          <Button
            size={'sm'}
            type={'icon'}
            onClick={() => {
              setCurrId(item.id)
              setEmpName(item.employee_name)
              setEmpEmail(item.employee_email)
              setEmpPhone(item.employee_phone)
              setJobTitle(item.job_title)
              setSalary(item.hourly_salary)
              setShowUpdate(true)
            }}
          >
            <FaPen size={15} />
          </Button>
        )
      },
    },
  ]

  const materialTheme = getTheme(DEFAULT_OPTIONS)
  const COLUMN_THEME = {
    Table: `
      --data-table-library_grid-template-columns: 20% 25% 20% 20% 10% 5%;
    `,
  }
  const theme = useTheme([materialTheme, TABLE_THEME, COLUMN_THEME])

  const pagination = usePagination(displayedData, {
    state: {
      page: 0,
      size: 5,
    },
  })

  async function renderTable() {
    let employeeRes = await axios.get(`${SERVER_URL}/employees`)

    if (employeeRes) {
      setDisplayedData({ nodes: employeeRes.data })
      setStoredData({ nodes: employeeRes.data })
    }
  }

  async function addEmployee() {
    setIsLoading(true)

    if (empName && empEmail && empPhone && jobTitle && salary) {
      await axios.post(`${SERVER_URL}/employees`, {
        employee_name: empName,
        employee_email: empEmail,
        employee_phone: empPhone,
        job_title: jobTitle,
        is_manager: jobTitle === 'Manager' ? true : false,
        hourly_salary: parseFloat(salary),
      })

      renderTable()
    }

    setIsLoading(false)
    setShowAdd(false)

    openSnackbar(`${empName} Added`)
  }

  async function updateEmployee() {
    setIsLoading(true)

    await axios.put(`${SERVER_URL}/employees`, {
      id: currId,
      employee_name: empName,
      employee_email: empEmail,
      employee_phone: empPhone,
      job_title: jobTitle,
      is_manager: jobTitle === 'Manager' ? true : false,
      hourly_salary: parseFloat(salary),
    })

    setCurrId('')

    renderTable()

    setIsLoading(false)
    setShowUpdate(false)

    openSnackbar(`${empName} Updated`)
  }

  return (
    <>
      <HeaderLarge>Employees</HeaderLarge>

      <ManageTable
        searchProps={{
          name: 'Employees',
          property: 'employee_name',
          data: storedData,
          setData: setDisplayedData,
        }}
        columns={columns}
        displayedData={displayedData}
        theme={theme}
        pagination={pagination}
      />

      <Modal
        show={showAdd}
        setShow={setShowAdd}
        header={'Add Employee'}
        fields={fields}
        buttonProps={addButtonProps}
      />

      <Modal
        show={showUpdate}
        setShow={setShowUpdate}
        header={'Update Employee'}
        fields={fields}
        buttonProps={updateButtonProps}
      />
    </>
  )
}

export default ManageEmployees
